import React from 'react'
import styled from 'styled-components'
import { useFetch } from '../component-items/helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus, faUserMinus, faEdit, faPlus, faMinus, faHistory } from '@fortawesome/pro-duotone-svg-icons'

// Types
interface AuditLogEntry {
  id: number
  actor: number | null
  actor_name: string | null
  target_user: number | null
  target_user_name: string | null
  action: string
  target_model: string
  changes: {
    old?: any
    new?: any
    role_name?: string
    segment?: string
    access_level?: string
    [key: string]: any
  }
  reason: string
  timestamp: string
}

interface UserAuditHistoryCardProps {
  userId: number
  /** Max number of entries to show */
  limit?: number
}

// Styled components
const Card = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const CardHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  svg {
    color: #6c757d;
  }
`

const CardBody = styled.div`
  padding: 1rem 1.25rem;
  flex: 1;
  overflow-y: auto;
`

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const TimelineEntry = styled.div`
  position: relative;
  padding-left: 1rem;
  border-left: 2px solid #e9ecef;

  &:last-child {
    border-left-color: transparent;
  }

  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #6c757d;
  }
`

const EntryDate = styled.div`
  font-size: 0.8em;
  color: #6c757d;
  margin-bottom: 0.25rem;
`

const EntryActor = styled.div`
  font-size: 0.85em;
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.35rem;
`

const EntryContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85em;
`

const ActionBadge = styled.span<{ $color: string; $bgColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2em 0.5em;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 500;
  background-color: ${(props) => props.$bgColor};
  color: ${(props) => props.$color};
  white-space: nowrap;
`

const EntryDetails = styled.span`
  color: #495057;
`

const EntryReason = styled.div`
  font-size: 0.8em;
  color: #6c757d;
  font-style: italic;
  margin-top: 0.25rem;
`

const CardFooter = styled.div`
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #e9ecef;
  font-size: 0.8em;
  color: #6c757d;
  text-align: center;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6c757d;
  text-align: center;

  svg {
    font-size: 2rem;
    margin-bottom: 0.75rem;
    opacity: 0.4;
  }

  p {
    margin: 0;
    font-size: 0.9em;
  }
`

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6c757d;
  font-size: 0.9em;
`

// Action type configuration
const ACTION_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: any }> = {
  assign_role: { label: 'Assigned Role', color: '#0d6efd', bgColor: '#cfe2ff', icon: faUserPlus },
  remove_role: { label: 'Removed Role', color: '#dc3545', bgColor: '#f8d7da', icon: faUserMinus },
  update_role: { label: 'Updated Role', color: '#6f42c1', bgColor: '#e2d9f3', icon: faEdit },
  add_override: { label: 'Added Override', color: '#198754', bgColor: '#d1e7dd', icon: faPlus },
  update_override: { label: 'Updated Override', color: '#fd7e14', bgColor: '#ffe5d0', icon: faEdit },
  remove_override: { label: 'Removed Override', color: '#6c757d', bgColor: '#e9ecef', icon: faMinus },
}

// Format timestamp
const formatTimestamp = (dateString: string) => {
  const date = new Date(dateString)
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${dateStr} · ${timeStr}`
}

// Format details
const formatDetails = (entry: AuditLogEntry): string => {
  const { action, changes } = entry

  if (action === 'assign_role' || action === 'remove_role') {
    const roleName = changes.new?.role_name || changes.old?.role_name || changes.role_name
    return `Role: ${roleName || 'Unknown'}`
  }

  if (action === 'update_role') {
    const oldRole = changes.old?.role_name
    const newRole = changes.new?.role_name
    if (oldRole && newRole) {
      return `${oldRole} → ${newRole}`
    }
    return `Role: ${newRole || oldRole || 'Unknown'}`
  }

  if (action === 'add_override' || action === 'remove_override') {
    const segment = changes.new?.segment || changes.old?.segment || changes.segment
    const level = changes.new?.access_level || changes.old?.access_level || changes.access_level
    return `${segment} → ${level || 'removed'}`
  }

  if (action === 'update_override') {
    const segment = changes.new?.segment || changes.old?.segment
    const oldLevel = changes.old?.access_level
    const newLevel = changes.new?.access_level
    if (oldLevel && newLevel) {
      return `${segment}: ${oldLevel} → ${newLevel}`
    }
    return `${segment} → ${newLevel || 'updated'}`
  }

  return ''
}

export const UserAuditHistoryCard = ({ userId, limit = 5 }: UserAuditHistoryCardProps) => {
  const apiUrl = `/api/rbac/audit-logs/?target_user_id=${userId}&page_size=${limit}`

  const { response, loaded, error }: any = useFetch(apiUrl, {})
  const entries: AuditLogEntry[] = loaded ? response?.results || [] : []

  return (
    <Card>
      <CardHeader>
        <FontAwesomeIcon icon={faHistory} />
        <h4>Permission History</h4>
      </CardHeader>

      <CardBody>
        {!loaded ? (
          <LoadingState>Loading history...</LoadingState>
        ) : error ? (
          <EmptyState>
            <FontAwesomeIcon icon={faHistory} />
            <p>Unable to load history</p>
          </EmptyState>
        ) : entries.length === 0 ? (
          <EmptyState>
            <FontAwesomeIcon icon={faHistory} />
            <p>No permission changes recorded yet</p>
          </EmptyState>
        ) : (
          <Timeline>
            {entries.map((entry) => {
              const actionConfig = ACTION_CONFIG[entry.action] || {
                label: entry.action,
                color: '#6c757d',
                bgColor: '#e9ecef',
                icon: faEdit,
              }

              return (
                <TimelineEntry key={entry.id}>
                  <EntryDate>{formatTimestamp(entry.timestamp)}</EntryDate>
                  <EntryActor>{entry.actor_name || 'System'}</EntryActor>
                  <EntryContent>
                    <ActionBadge $color={actionConfig.color} $bgColor={actionConfig.bgColor}>
                      <FontAwesomeIcon icon={actionConfig.icon} />
                      {actionConfig.label}
                    </ActionBadge>
                    <EntryDetails>{formatDetails(entry)}</EntryDetails>
                  </EntryContent>
                  {entry.reason && <EntryReason>{entry.reason}</EntryReason>}
                </TimelineEntry>
              )
            })}
          </Timeline>
        )}
      </CardBody>

      {loaded && entries.length > 0 && <CardFooter>Showing {entries.length} most recent</CardFooter>}
    </Card>
  )
}
